import {
  authFetch,
  captureAuthSessionOwner,
  getWebDeviceId,
  isAuthSessionOwnerCurrent,
  type AuthSessionOwner,
} from "@/shared/auth/session";
import { createWebIdempotencyKey } from "@/shared/auth/web-device-id.mjs";
import {
  applySyncSavedArticleDeletions,
  applySyncSavedArticles,
  loadSavedArticles,
  savedArticlesToSyncPayload,
  type SyncSavedArticle,
} from "@/shared/saved/store";
import {
  applySyncTaskDeletions,
  applySyncTasks,
  loadTasks,
  tasksToSyncPayload,
  type SyncTodo,
} from "@/shared/tasks/store";
import { jsonFromResponse } from "./client";
import {
  applySyncPreferences,
  getSyncSince,
  hasStoredHiddenDefaultStockSymbolsPreference,
  loadPreferences,
  preferencesToSyncPayload,
  setSyncSince,
} from "@/shared/preferences/store";
import {
  applySyncWatchlist,
  hiddenDefaultStockSymbolsForSync,
  watchlistSymbolsForSync,
} from "@/shared/markets/watchlist";
import {
  applyThemeSyncPayload,
  readThemeSyncPayload,
  type ThemeSyncPayload,
} from "@/shared/theme/theme-storage";
import {
  advancePendingSyncDeletionClocks,
  acknowledgeSyncDeletionMutations,
  pendingSyncDeletions,
  reconcilePulledSyncDeletions,
  type SyncSavedArticleDeletion,
  type SyncTodoDeletion,
} from "@/shared/sync/deletion-outbox";
import {
  applyRecordPatch,
  applyWatchlistPatch,
  buildRecordPatch,
  buildWatchlistPatch,
  captureSyncV2LocalAncestors,
  hasRecordEntries,
  hasWatchlistChanges,
  isSyncV2RemoteBaselineReady,
  markSyncV2PendingDomains,
  markSyncV2RemoteBaselineReady,
  normalizeSyncV2Baseline,
  pendingSyncV2Options,
  reconcileRecordPull,
  reconcileWatchlistPull,
  shouldApplySyncAcknowledgement,
  syncTimestampEpochMs,
  syncValuesEqual,
  type SyncV2Baseline,
} from "@/shared/sync/sync-v2-patch.mjs";
import {
  readAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import { applyBriefStreakSyncPayload, loadBriefStreakSyncState } from "@/shared/brief/run-store";
import { observePulledRowMutationClocks } from "@/shared/sync/account-row-mutation-clock";
import { contentRowAcknowledgementFailure } from "@/shared/sync/content-row-ack.mjs";
import { readCompleteSyncPullBundle } from "@/shared/sync/pull-bundle-contract.mjs";
import { assertLocalDataBootstrapCompletionResponse } from "./bootstrap-sync-response.mjs";

export type SyncBundle = {
  preferences?: Record<string, unknown> | null;
  preferencesUpdatedAt?: string | null;
  serverTime?: string;
  audioPreferences?: { pushEnabled?: boolean; emailDigest?: boolean } | null;
  theme?: Partial<ThemeSyncPayload> | Record<string, unknown> | null;
  themeUpdatedAt?: string | null;
  watchlist?: string[] | null;
  watchlistUpdatedAt?: string | null;
  streak?: Record<string, unknown> | null;
  streakUpdatedAt?: string | null;
  todos?: SyncTodo[];
  deletedTodos?: SyncTodoDeletion[];
  savedArticles?: SyncSavedArticle[];
  deletedSavedArticles?: SyncSavedArticleDeletion[];
};

export type PushSyncOptions = {
  includePreferences?: boolean;
  includeTheme?: boolean;
  includeTasks?: boolean;
  includeSavedArticles?: boolean;
  includeWatchlist?: boolean;
  includeStreak?: boolean;
};

type SyncRequestOptions = {
  expectedOwner?: AuthSessionOwner | null;
  completeLocalDataBootstrap?: boolean;
  signal?: AbortSignal;
  onBeforeApply?: () => void;
  onAfterApply?: () => void;
  assertCanApply?: () => void;
  preserveLocalTasks?: boolean;
  preserveLocalSavedArticles?: boolean;
};

export type PreparedSyncPushRequest = Readonly<{
  version: 1;
  idempotencyKey: string;
  bodyJson: string;
}>;

const LOCAL_DATA_BOOTSTRAP_DOMAINS = [
  "preferences",
  "theme",
  "watchlist",
  "streak",
  "audioPreferences",
  "todos",
  "deletedTodos",
  "savedArticles",
  "deletedSavedArticles",
] as const;
const SYNC_V2_BASELINE_KEY = "tb_public_web_sync_v2_baseline_v1";
const THEME_PATCH_KEYS = [
  "uiTheme",
  "appThemePalette",
  "themeAutoTransitionEnabled",
  "highContrast",
] as const;
type SnapshotDomain = keyof SyncV2Baseline["domains"];

let serializedPushTail: Promise<void> = Promise.resolve();

function codedSyncError(code: string) {
  const error = new Error(code) as Error & { code: string };
  error.code = code;
  return error;
}

function requiredSyncOwner(expectedOwner: AuthSessionOwner | null | undefined): AuthSessionOwner {
  const owner = expectedOwner || captureAuthSessionOwner();
  if (!owner) throw codedSyncError("auth_session_mutation_superseded");
  return owner;
}

function serializeSyncPush<T>(work: () => Promise<T>): Promise<T> {
  const run = serializedPushTail.then(work, work);
  serializedPushTail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

function markDomainsPending(
  baseline: SyncV2Baseline,
  domains: SnapshotDomain[],
  updatedAtEpochMs: number,
) {
  const timestamp = Math.max(1, Math.trunc(updatedAtEpochMs));
  for (const domain of domains) {
    const state = baseline.domains[domain];
    state.pending = true;
    state.localUpdatedAtEpochMs = Math.max(state.localUpdatedAtEpochMs, timestamp);
  }
}

export function markSyncV2DomainsPending(opts: PushSyncOptions, updatedAtEpochMs = Date.now()) {
  writeSyncV2Baseline(markSyncV2PendingDomains(readSyncV2Baseline(), opts, updatedAtEpochMs));
}

export function pendingSyncV2PushOptions(): PushSyncOptions {
  return pendingSyncV2Options(readSyncV2Baseline()) as PushSyncOptions;
}

function preferencesSnapshotForSync() {
  const preferences = loadPreferences();
  if (hasStoredHiddenDefaultStockSymbolsPreference()) {
    return preferencesToSyncPayload(preferences);
  }
  return preferencesToSyncPayload({
    ...preferences,
    hiddenDefaultStockSymbols: hiddenDefaultStockSymbolsForSync(),
  });
}

export function captureSyncV2LocalStateBeforeInitialPull() {
  const baseline = captureSyncV2LocalAncestors(readSyncV2Baseline(), {
    preferences: preferencesSnapshotForSync(),
    theme: readThemeSyncPayload(),
    watchlist: watchlistSymbolsForSync(),
  });
  writeSyncV2Baseline(baseline);
}

export function hasSyncV2RemoteBaseline(): boolean {
  return isSyncV2RemoteBaselineReady(readSyncV2Baseline());
}

function requestTimestamp(
  baseline: SyncV2Baseline,
  domain: SnapshotDomain,
  capturedAtEpochMs: number,
): string {
  const state = baseline.domains[domain];
  if (!state.pending || state.localUpdatedAtEpochMs <= 0) {
    markDomainsPending(baseline, [domain], capturedAtEpochMs);
  }
  return new Date(baseline.domains[domain].localUpdatedAtEpochMs).toISOString();
}

function applyEffectiveRemoteWinners(
  baseline: Record<string, unknown>,
  effective: Record<string, unknown>,
  keys: string[],
) {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(effective, key)) {
      baseline[key] = effective[key];
    }
  }
}

function settleRecordDomainAfterPull(args: {
  baseline: SyncV2Baseline;
  domain: "preferences" | "theme";
  effective: Record<string, unknown>;
  remoteBaseline: Record<string, unknown>;
  remoteWinnerKeys: string[];
  allowedKeys?: string[];
  remoteUpdatedAt?: string | null;
}) {
  applyEffectiveRemoteWinners(args.remoteBaseline, args.effective, args.remoteWinnerKeys);
  if (args.domain === "preferences") args.baseline.preferences = args.remoteBaseline;
  else args.baseline.theme = args.remoteBaseline;
  const pending = buildRecordPatch(args.effective, args.remoteBaseline, args.allowedKeys);
  const state = args.baseline.domains[args.domain];
  state.remoteUpdatedAtEpochMs = Math.max(
    state.remoteUpdatedAtEpochMs,
    syncTimestampEpochMs(args.remoteUpdatedAt),
  );
  state.pending = hasRecordEntries(pending);
  const resolvedPendingClock = Math.max(state.localUpdatedAtEpochMs, state.remoteUpdatedAtEpochMs);
  state.localUpdatedAtEpochMs = state.pending
    ? resolvedPendingClock || Date.now()
    : state.remoteUpdatedAtEpochMs;
}

function readSyncV2Baseline(): SyncV2Baseline {
  try {
    const raw = readAccountScopedStorage(SYNC_V2_BASELINE_KEY);
    return normalizeSyncV2Baseline(raw ? JSON.parse(raw) : null);
  } catch {
    return normalizeSyncV2Baseline(null);
  }
}

function writeSyncV2Baseline(baseline: SyncV2Baseline) {
  try {
    writeAccountScopedStorage(
      SYNC_V2_BASELINE_KEY,
      JSON.stringify(normalizeSyncV2Baseline(baseline)),
    );
  } catch {
    // Losing a baseline only makes a later compatibility push larger. The v2
    // patch still merges and must not turn a successful network write into an
    // apparent failure.
  }
}

function reconcilePulledSnapshotDomains(bundle: SyncBundle) {
  const baseline = readSyncV2Baseline();
  let changed = false;
  if (bundle.preferences) {
    const incomingPreferences = { ...bundle.preferences };
    const legacyCountry =
      typeof incomingPreferences.country === "string" ? incomingPreferences.country : "";
    const mustRepairMarketCountry =
      !Object.prototype.hasOwnProperty.call(incomingPreferences, "marketCountry") &&
      legacyCountry.trim().length > 0;
    if (mustRepairMarketCountry) {
      incomingPreferences.marketCountry = legacyCountry.trim().toUpperCase();
    }
    const state = baseline.domains.preferences;
    const merge = reconcileRecordPull({
      current: preferencesSnapshotForSync(),
      baseline: baseline.preferences,
      remote: incomingPreferences,
      localUpdatedAt: state.localUpdatedAtEpochMs,
      remoteUpdatedAt: bundle.preferencesUpdatedAt,
    });
    const resolvedPreferences = { ...merge.resolved };
    if (
      !hasStoredHiddenDefaultStockSymbolsPreference() &&
      !Object.prototype.hasOwnProperty.call(incomingPreferences, "hiddenDefaultStockSymbols")
    ) {
      // A normalized empty string is not migration authority. Keep the
      // canonical field absent until the cloud owns it or the user actually
      // changes the watchlist, so the legacy row can still supply its hidden
      // country defaults.
      delete resolvedPreferences.hiddenDefaultStockSymbols;
    }
    applySyncPreferences(resolvedPreferences, {
      remoteOwnedKeys: Object.keys(incomingPreferences),
    });
    const effective = preferencesSnapshotForSync();
    settleRecordDomainAfterPull({
      baseline,
      domain: "preferences",
      effective,
      remoteBaseline: merge.baseline,
      remoteWinnerKeys: merge.remoteWinnerKeys,
      remoteUpdatedAt: bundle.preferencesUpdatedAt,
    });
    if (mustRepairMarketCountry && baseline.preferences) {
      delete baseline.preferences.marketCountry;
      state.pending = true;
      state.localUpdatedAtEpochMs =
        Math.max(state.localUpdatedAtEpochMs, state.remoteUpdatedAtEpochMs) || Date.now();
    }
    changed = true;
  }
  if (bundle.theme) {
    const state = baseline.domains.theme;
    const merge = reconcileRecordPull({
      current: readThemeSyncPayload(),
      baseline: baseline.theme,
      remote: bundle.theme,
      allowedKeys: [...THEME_PATCH_KEYS],
      localUpdatedAt: state.localUpdatedAtEpochMs,
      remoteUpdatedAt: bundle.themeUpdatedAt,
    });
    applyThemeSyncPayload(merge.resolved);
    settleRecordDomainAfterPull({
      baseline,
      domain: "theme",
      effective: readThemeSyncPayload(),
      remoteBaseline: merge.baseline,
      remoteWinnerKeys: merge.remoteWinnerKeys,
      allowedKeys: [...THEME_PATCH_KEYS],
      remoteUpdatedAt: bundle.themeUpdatedAt,
    });
    changed = true;
  }
  if (Array.isArray(bundle.watchlist)) {
    const state = baseline.domains.watchlist;
    const merge = reconcileWatchlistPull({
      current: watchlistSymbolsForSync(),
      baseline: baseline.watchlist,
      remote: bundle.watchlist,
      localUpdatedAt: state.localUpdatedAtEpochMs,
      remoteUpdatedAt: bundle.watchlistUpdatedAt,
    });
    baseline.watchlist = merge.baseline;
    applySyncWatchlist(merge.resolved);
    const effective = watchlistSymbolsForSync();
    const pending = buildWatchlistPatch(effective, merge.baseline);
    state.remoteUpdatedAtEpochMs = Math.max(
      state.remoteUpdatedAtEpochMs,
      syncTimestampEpochMs(bundle.watchlistUpdatedAt),
    );
    state.pending = hasWatchlistChanges(pending);
    const resolvedPendingClock = Math.max(
      state.localUpdatedAtEpochMs,
      state.remoteUpdatedAtEpochMs,
    );
    state.localUpdatedAtEpochMs = state.pending
      ? resolvedPendingClock || Date.now()
      : state.remoteUpdatedAtEpochMs;
    changed = true;
  }
  if (changed || !baseline.remoteBaselineReady) {
    writeSyncV2Baseline(markSyncV2RemoteBaselineReady(baseline));
  }
}

function responseDomainResult(
  response: Record<string, unknown>,
  domain: SnapshotDomain | "streak",
): Record<string, unknown> | null {
  const results =
    response.results && typeof response.results === "object" && !Array.isArray(response.results)
      ? (response.results as Record<string, unknown>)
      : null;
  const value = results?.[domain];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function settleDomainAfterAcknowledgement(baseline: SyncV2Baseline, domain: SnapshotDomain) {
  const state = baseline.domains[domain];
  if (domain === "preferences") {
    const pending = buildRecordPatch(preferencesSnapshotForSync(), baseline.preferences);
    state.pending = hasRecordEntries(pending);
  } else if (domain === "theme") {
    const pending = buildRecordPatch(readThemeSyncPayload(), baseline.theme, [...THEME_PATCH_KEYS]);
    state.pending = hasRecordEntries(pending);
  } else {
    const pending = buildWatchlistPatch(watchlistSymbolsForSync(), baseline.watchlist);
    state.pending = hasWatchlistChanges(pending);
  }
  if (!state.pending) state.localUpdatedAtEpochMs = state.remoteUpdatedAtEpochMs;
}

function acknowledgePreparedSnapshotDomains(
  body: Record<string, unknown>,
  response: Record<string, unknown>,
) {
  const baseline = readSyncV2Baseline();
  const responseAt = syncTimestampEpochMs(response.serverTime) || Date.now();
  let changed = false;
  let preferenceConflict = false;
  let themeConflict = false;
  let watchlistConflict = false;
  let streakConflict = false;
  if (body.completeLocalDataBootstrap === true) {
    baseline.remoteBaselineReady = true;
    changed = true;
    if (body.preferences && typeof body.preferences === "object") {
      baseline.preferences = body.preferences as Record<string, unknown>;
      const state = baseline.domains.preferences;
      state.remoteUpdatedAtEpochMs = syncTimestampEpochMs(body.preferencesUpdatedAt) || responseAt;
      // Preference LWW is ordered by the frozen request clock. A local edit
      // made while bootstrap is in flight can predate the HTTP response but
      // must still acknowledge as the required late-edit delta.
      state.acknowledgedAtEpochMs = state.remoteUpdatedAtEpochMs;
      settleDomainAfterAcknowledgement(baseline, "preferences");
      changed = true;
    }
    if (body.theme && typeof body.theme === "object") {
      baseline.theme = body.theme as Record<string, unknown>;
      const state = baseline.domains.theme;
      state.remoteUpdatedAtEpochMs = syncTimestampEpochMs(body.themeUpdatedAt) || responseAt;
      // Theme patches use the frozen request clock, just like preferences.
      // A faster server clock must not pin out an edit made during bootstrap.
      state.acknowledgedAtEpochMs = state.remoteUpdatedAtEpochMs;
      settleDomainAfterAcknowledgement(baseline, "theme");
      changed = true;
    }
    if (Array.isArray(body.watchlist)) {
      baseline.watchlist = body.watchlist as string[];
      const state = baseline.domains.watchlist;
      state.remoteUpdatedAtEpochMs = syncTimestampEpochMs(body.watchlistUpdatedAt) || responseAt;
      state.acknowledgedAtEpochMs = responseAt;
      settleDomainAfterAcknowledgement(baseline, "watchlist");
      changed = true;
    }
  } else {
    if (body.preferencesPatch && typeof body.preferencesPatch === "object") {
      const result = responseDomainResult(response, "preferences");
      if (result?.merged !== true) {
        preferenceConflict = true;
      } else {
        const state = baseline.domains.preferences;
        const requestAt = syncTimestampEpochMs(body.preferencesUpdatedAt) || responseAt;
        if (shouldApplySyncAcknowledgement(state, requestAt)) {
          baseline.preferences = applyRecordPatch(baseline.preferences, body.preferencesPatch);
          state.remoteUpdatedAtEpochMs = Math.max(state.remoteUpdatedAtEpochMs, requestAt);
          state.acknowledgedAtEpochMs = requestAt;
        }
      }
      settleDomainAfterAcknowledgement(baseline, "preferences");
      changed = true;
    }
    if (body.themePatch && typeof body.themePatch === "object") {
      const result = responseDomainResult(response, "theme");
      if (result?.merged !== true) {
        themeConflict = true;
      } else {
        const state = baseline.domains.theme;
        const requestAt = syncTimestampEpochMs(body.themeUpdatedAt) || responseAt;
        if (shouldApplySyncAcknowledgement(state, requestAt)) {
          baseline.theme = applyRecordPatch(baseline.theme, body.themePatch);
          state.remoteUpdatedAtEpochMs = Math.max(state.remoteUpdatedAtEpochMs, requestAt);
          state.acknowledgedAtEpochMs = requestAt;
        }
      }
      settleDomainAfterAcknowledgement(baseline, "theme");
      changed = true;
    }
    if (body.watchlistPatch && typeof body.watchlistPatch === "object") {
      const result = responseDomainResult(response, "watchlist");
      if (result?.merged !== true) {
        watchlistConflict = true;
      } else {
        const state = baseline.domains.watchlist;
        if (shouldApplySyncAcknowledgement(state, responseAt)) {
          baseline.watchlist = applyWatchlistPatch(baseline.watchlist, body.watchlistPatch);
          state.remoteUpdatedAtEpochMs = Math.max(state.remoteUpdatedAtEpochMs, responseAt);
          state.acknowledgedAtEpochMs = responseAt;
        }
      }
      settleDomainAfterAcknowledgement(baseline, "watchlist");
      changed = true;
    }
  }
  if (body.streak && typeof body.streak === "object") {
    const result = responseDomainResult(response, "streak");
    streakConflict = result?.merged !== true;
  }
  if (changed) writeSyncV2Baseline(baseline);
  return { preferenceConflict, themeConflict, watchlistConflict, streakConflict };
}

function assertExpectedOwnerCurrent(expectedOwner: AuthSessionOwner | null | undefined) {
  if (!expectedOwner || isAuthSessionOwnerCurrent(expectedOwner)) return;
  const error = new Error("auth_session_mutation_superseded") as Error & {
    code: string;
  };
  error.code = "auth_session_mutation_superseded";
  throw error;
}

export async function pullSync(options: unknown = {}): Promise<SyncBundle> {
  const requestOptions =
    options && typeof options === "object" ? (options as SyncRequestOptions) : {};
  const expectedOwner = requiredSyncOwner(requestOptions.expectedOwner);
  assertExpectedOwnerCurrent(expectedOwner);
  const since = getSyncSince();
  const incremental = Boolean(since);
  const taskRowsBeforeRequest = loadTasks();
  const savedArticleRowsBeforeRequest = loadSavedArticles();
  const tasksBeforeRequest = tasksToSyncPayload(taskRowsBeforeRequest);
  const savedArticlesBeforeRequest = savedArticlesToSyncPayload(savedArticleRowsBeforeRequest);
  const streakBeforeRequest = loadBriefStreakSyncState();
  const q = since ? `?since=${encodeURIComponent(since)}` : "";
  const res = await authFetch(`/api/auth/sync/pull${q}`, {
    activitySource: "background",
    signal: requestOptions.signal,
  });
  const data = await jsonFromResponse<SyncBundle & { bundle?: SyncBundle }>(res, {
    fallbackCode: "sync_pull_failed",
  });
  const candidate =
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    Object.prototype.hasOwnProperty.call(data, "bundle")
      ? data.bundle
      : data;
  const bundle = readCompleteSyncPullBundle<SyncBundle>(candidate);
  assertExpectedOwnerCurrent(expectedOwner);
  requestOptions.assertCanApply?.();
  try {
    requestOptions.onBeforeApply?.();
    observePulledRowMutationClocks(bundle);
    advancePendingSyncDeletionClocks(bundle.todos, bundle.savedArticles);
    const pendingDeletions = pendingSyncDeletions();
    const tasksChangedWhilePulling = !syncValuesEqual(tasksToSyncPayload(), tasksBeforeRequest);
    const savedArticlesChangedWhilePulling = !syncValuesEqual(
      savedArticlesToSyncPayload(),
      savedArticlesBeforeRequest,
    );
    const streakChangedWhilePulling = !syncValuesEqual(
      loadBriefStreakSyncState(),
      streakBeforeRequest,
    );
    reconcilePulledSnapshotDomains(bundle);
    if (bundle.streak && typeof bundle.streak === "object") {
      applyBriefStreakSyncPayload(
        bundle.streak,
        bundle.streakUpdatedAt,
        bundle.serverTime,
        !incremental && !streakChangedWhilePulling,
      );
    }
    if (Array.isArray(bundle.todos)) {
      applySyncTasks(bundle.todos, {
        merge:
          incremental || requestOptions.preserveLocalTasks === true || tasksChangedWhilePulling,
        authoritativeFullPull: !incremental && requestOptions.preserveLocalTasks !== true,
        authoritativeBaseline: taskRowsBeforeRequest,
        preferIncomingWhenLocalUnchanged: requestOptions.preserveLocalTasks !== true,
        preserveExistingLocalRows: requestOptions.preserveLocalTasks === true,
      });
    }
    if (Array.isArray(bundle.savedArticles)) {
      applySyncSavedArticles(bundle.savedArticles, {
        merge:
          incremental ||
          requestOptions.preserveLocalSavedArticles === true ||
          savedArticlesChangedWhilePulling,
        authoritativeFullPull: !incremental && requestOptions.preserveLocalSavedArticles !== true,
        authoritativeBaseline: savedArticleRowsBeforeRequest,
        preferIncomingWhenLocalUnchanged:
          requestOptions.preserveLocalSavedArticles !== true,
        preserveExistingLocalRows: requestOptions.preserveLocalSavedArticles === true,
      });
    }
    applySyncTaskDeletions(bundle.deletedTodos, {
      respectLocalUpdatedAt: true,
      authoritativeBaseline: taskRowsBeforeRequest,
      preferDeletionWhenLocalUnchanged: requestOptions.preserveLocalTasks !== true,
      preserveExistingLocalRows: requestOptions.preserveLocalTasks === true,
    });
    applySyncSavedArticleDeletions(bundle.deletedSavedArticles, {
      respectLocalUpdatedAt: true,
      authoritativeBaseline: savedArticleRowsBeforeRequest,
      preferDeletionWhenLocalUnchanged:
        requestOptions.preserveLocalSavedArticles !== true,
      preserveExistingLocalRows: requestOptions.preserveLocalSavedArticles === true,
    });
    // The local outbox represents this browser's own explicit delete intent,
    // so it remains unconditional until the server acknowledges its mutation id.
    applySyncTaskDeletions(pendingDeletions.deletedTodos);
    applySyncSavedArticleDeletions(pendingDeletions.deletedSavedArticles);
    reconcilePulledSyncDeletions(bundle.deletedTodos, bundle.deletedSavedArticles);
    setSyncSince(bundle.serverTime);
  } finally {
    requestOptions.onAfterApply?.();
  }
  return bundle;
}

export function prepareSyncPushRequest(
  opts: PushSyncOptions = {},
  options: Pick<SyncRequestOptions, "completeLocalDataBootstrap"> = {},
): PreparedSyncPushRequest {
  const capturedAtEpochMs = Date.now();
  const capturedAt = new Date(capturedAtEpochMs).toISOString();
  const pendingDeletions = pendingSyncDeletions();
  const generalPush = Object.keys(opts).length === 0;
  const fullLocalDataBootstrap = options.completeLocalDataBootstrap === true;
  const baseline = readSyncV2Baseline();
  const streak = loadBriefStreakSyncState();
  const body: Record<string, unknown> = {
    syncProtocolVersion: 2,
    deviceId: getWebDeviceId(),
  };
  if (fullLocalDataBootstrap) {
    body.completeLocalDataBootstrap = true;
    body.localDataBootstrapManifest = {
      version: 1,
      domains: [...LOCAL_DATA_BOOTSTRAP_DOMAINS],
    };
    body.streak = streak.lastDate
      ? { count: streak.count, best: streak.best, lastDate: streak.lastDate }
      : null;
    if (streak.updatedAtEpochMs > 0) {
      body.streakUpdatedAt = new Date(streak.updatedAtEpochMs).toISOString();
    }
    // Audio preferences still have no separate public-web store. Explicit
    // null distinguishes "known empty" from an accidentally partial push.
    body.audioPreferences = null;
  }
  // A watchlist mutation has two canonical halves: explicit extra symbols in
  // the watchlist document and hidden country defaults in preferences.
  const includePreferences =
    generalPush || opts.includePreferences === true || opts.includeWatchlist === true;
  if (includePreferences) {
    const preferences = preferencesSnapshotForSync();
    if (fullLocalDataBootstrap) {
      body.preferences = preferences;
      body.preferencesUpdatedAt = capturedAt;
    } else {
      const preferencesPatch = buildRecordPatch(preferences, baseline.preferences);
      if (hasRecordEntries(preferencesPatch)) {
        // Compatibility snapshot for v1 servers; v2 servers consume only the
        // patch and preserve disjoint keys written by Android or another tab.
        body.preferences = preferences;
        body.preferencesPatch = preferencesPatch;
        body.preferencesUpdatedAt = requestTimestamp(baseline, "preferences", capturedAtEpochMs);
      } else {
        baseline.domains.preferences.pending = false;
      }
    }
  }
  if (generalPush || opts.includeTheme) {
    const theme = readThemeSyncPayload();
    if (fullLocalDataBootstrap) {
      body.theme = theme;
      body.themeUpdatedAt = capturedAt;
    } else {
      const themePatch = buildRecordPatch(theme, baseline.theme, [...THEME_PATCH_KEYS]);
      if (hasRecordEntries(themePatch)) {
        body.theme = theme;
        body.themePatch = themePatch;
        body.themeUpdatedAt = requestTimestamp(baseline, "theme", capturedAtEpochMs);
      } else {
        baseline.domains.theme.pending = false;
      }
    }
  }
  if (opts.includeTasks) body.todos = tasksToSyncPayload();
  if (opts.includeSavedArticles) body.savedArticles = savedArticlesToSyncPayload();
  if (!fullLocalDataBootstrap && (generalPush || opts.includeStreak) && streak.lastDate) {
    body.streak = { count: streak.count, best: streak.best, lastDate: streak.lastDate };
    body.streakUpdatedAt = new Date(streak.updatedAtEpochMs || capturedAtEpochMs).toISOString();
  }
  if (pendingDeletions.deletedTodos.length || fullLocalDataBootstrap) {
    body.deletedTodos = pendingDeletions.deletedTodos;
  }
  if (pendingDeletions.deletedSavedArticles.length || fullLocalDataBootstrap) {
    body.deletedSavedArticles = pendingDeletions.deletedSavedArticles;
  }
  if (opts.includeWatchlist) {
    const watchlist = watchlistSymbolsForSync();
    if (fullLocalDataBootstrap) {
      body.watchlist = watchlist;
      body.watchlistUpdatedAt = capturedAt;
    } else {
      const watchlistPatch = buildWatchlistPatch(watchlist, baseline.watchlist);
      if (hasWatchlistChanges(watchlistPatch)) {
        body.watchlist = watchlist;
        body.watchlistPatch = watchlistPatch;
        body.watchlistUpdatedAt = requestTimestamp(baseline, "watchlist", capturedAtEpochMs);
      } else {
        baseline.domains.watchlist.pending = false;
      }
    }
  }
  if (!fullLocalDataBootstrap) writeSyncV2Baseline(baseline);
  return Object.freeze({
    version: 1,
    idempotencyKey: createWebIdempotencyKey(),
    bodyJson: JSON.stringify(body),
  });
}

export async function pushPreparedSyncRequest(
  request: PreparedSyncPushRequest,
  options: Pick<SyncRequestOptions, "expectedOwner"> = {},
) {
  if (
    request?.version !== 1 ||
    typeof request.idempotencyKey !== "string" ||
    request.idempotencyKey.length < 16 ||
    typeof request.bodyJson !== "string"
  ) {
    throw new Error("invalid_prepared_sync_request");
  }
  let requestBody: Record<string, unknown>;
  try {
    const parsed = JSON.parse(request.bodyJson);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("invalid_prepared_sync_request");
    }
    requestBody = parsed as Record<string, unknown>;
  } catch {
    throw new Error("invalid_prepared_sync_request");
  }
  if (requestBody.completeLocalDataBootstrap !== true && !hasSyncV2RemoteBaseline()) {
    throw codedSyncError("sync_initial_pull_required");
  }
  const expectedOwner = requiredSyncOwner(options.expectedOwner);
  return serializeSyncPush(async () => {
    assertExpectedOwnerCurrent(expectedOwner);
    const res = await authFetch("/api/auth/sync/push", {
      activitySource: "background",
      method: "POST",
      headers: { "Idempotency-Key": request.idempotencyKey },
      // This exact journaled string is the server's raw fingerprint input.
      // Never re-stringify or inject fields on a response-loss retry.
      body: request.bodyJson,
    });
    const data = await jsonFromResponse<Record<string, unknown>>(res, {
      fallbackCode: "sync_push_failed",
    });
    assertExpectedOwnerCurrent(expectedOwner);
    assertLocalDataBootstrapCompletionResponse(request.bodyJson, data);
    const contentRowFailure = contentRowAcknowledgementFailure(requestBody, data);
    if (contentRowFailure) throw codedSyncError(contentRowFailure);
    const acknowledgement = acknowledgePreparedSnapshotDomains(requestBody, data);
    acknowledgeSyncDeletionMutations(data.acknowledgedMutationIds);
    if (acknowledgement.preferenceConflict) {
      throw codedSyncError("sync_preference_conflict");
    }
    if (acknowledgement.themeConflict) {
      throw codedSyncError("sync_theme_conflict");
    }
    if (acknowledgement.watchlistConflict) {
      throw codedSyncError("sync_watchlist_conflict");
    }
    if (acknowledgement.streakConflict) {
      throw codedSyncError("sync_streak_conflict");
    }
    return data;
  });
}

export async function pushSync(opts: PushSyncOptions = {}, options: SyncRequestOptions = {}) {
  const expectedOwner = requiredSyncOwner(options.expectedOwner);
  assertExpectedOwnerCurrent(expectedOwner);
  const request = prepareSyncPushRequest(opts, {
    completeLocalDataBootstrap: options.completeLocalDataBootstrap,
  });
  return pushPreparedSyncRequest(request, { expectedOwner });
}
